import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Link, Navigate, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ExternalLink, Settings } from 'lucide-react';
import ApprovalsPanel from './approvals/ApprovalsPanel';
import CondoSwitcher from './admin/CondoSwitcher';
import DashboardTab from './admin/DashboardTab';
import ResidentsTab from './admin/ResidentsTab';
import UnitsTab from './admin/UnitsTab';
import TicketsTab from './admin/TicketsTab';
import ForumTab from './admin/ForumTab';
import BulletinsTab from './admin/BulletinsTab';
import CalendarTab from './admin/CalendarTab';
import HandbookTab from './admin/HandbookTab';
import AmenitiesTab from './admin/AmenitiesTab';
import EmailsTab from './admin/EmailsTab';
import SelfManagementSettings from './admin/SelfManagementSettings';
import VendorsTab from './admin/VendorsTab';
import AdminSearch from './admin/AdminSearch';
import SearchResultsTab from './admin/SearchResultsTab';

const ADMIN_TABS = [
  { key: 'dashboard', label: 'Dashboard', component: DashboardTab },
  { key: 'approvals', label: 'Approvals', badge: 'approvals' },
  { key: 'residents', label: 'Residents', component: ResidentsTab, badge: 'registrations' },
  { key: 'units', label: 'Units', component: UnitsTab },
  { key: 'tickets', label: 'Tickets', component: TicketsTab, badge: 'openTickets', quiet: true },
  { key: 'forum', label: 'Forum', component: ForumTab },
  { key: 'bulletins', label: 'Bulletins', component: BulletinsTab },
  { key: 'calendar', label: 'Calendar', component: CalendarTab },
  { key: 'handbook', label: 'Handbook', component: HandbookTab },
  { key: 'amenities', label: 'Amenities', component: AmenitiesTab },
  { key: 'emails', label: 'Emails', component: EmailsTab },
  { key: 'vendors', label: 'Vendors', component: VendorsTab, selfManagement: true }
];

function AdminPage() {
  const navigate = useNavigate();
  const { condoSlug, tab: tabParam } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isAdmin, isActive, logout, getToken, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [condos, setCondos] = useState([]);
  const [condosLoaded, setCondosLoaded] = useState(false);
  const [lastCondoId, setLastCondoId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCondo, setEditingCondo] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', units: '', features: [] });
  const [condoFormFeatureInput, setCondoFormFeatureInput] = useState('');
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  const condo = condoSlug ? condos.find(c => c.slug === condoSlug) : null;
  const tab = tabParam || 'dashboard';

  const fetchCondos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${getApiBaseUrl()}/api/admin/condos`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCondos(response.data.condos || []);
      setLastCondoId(response.data.lastCondoId || null);
      setCondosLoaded(true);
    } catch (err) {
      console.error('Failed to fetch condos:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin && isActive) {
      fetchCondos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, isAdmin, isActive]);

  useEffect(() => {
    if (!condo?._id) return;
    axios.put(`${getApiBaseUrl()}/api/admin/last-condo`, { condoId: condo._id }, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }).catch(err => console.error('Failed to save last condo:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condo?._id]);

  const refreshCounts = useCallback(async () => {
    if (!condo?._id) return;
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/admin/condos/${condo._id}/tab-counts`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTabCounts(response.data.counts || {});
    } catch (err) {
      console.error('Failed to fetch tab counts:', err);
    }
  }, [condo?._id, getToken]);

  useEffect(() => {
    refreshCounts();
    setSuccessMessage(null);
  }, [refreshCounts, tab]);

  useEffect(() => {
    if (tab === 'settings' && condo) startEditing(condo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, condo]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      units: '',
      features: []
    });
    setCondoFormFeatureInput('');
    setShowAddForm(false);
    setEditingCondo(null);
    setFormError(null);
  };

  const handleAddCondo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/api/admin/condos`,
        {
          name: formData.name,
          address: formData.address,
          units: parseInt(formData.units)
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      if (response.data.success) {
        if (formData.features.length > 0) {
          await axios.put(
            `${getApiBaseUrl()}/api/admin/condos/${response.data.condo._id}/features`,
            { features: formData.features },
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
        }
        setSuccessMessage('Condo added successfully!');
        resetForm();
        fetchCondos();
      }
    } catch (err) {
      console.error('Failed to add condo:', err);
      setFormError(err.response?.data?.error || 'Failed to add condo');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCondo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      const updateData = {
        name: formData.name,
        address: formData.address,
        units: parseInt(formData.units)
      };

      const response = await axios.put(
        `${getApiBaseUrl()}/api/admin/condos/${editingCondo._id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      if (response.data.success) {
        await axios.put(
          `${getApiBaseUrl()}/api/admin/condos/${editingCondo._id}/features`,
          { features: formData.features },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setSuccessMessage('Condo updated successfully!');
        resetForm();
        if (response.data.condo?.slug && response.data.condo.slug !== editingCondo.slug) {
          navigate(`/admin/${response.data.condo.slug}/settings`, { replace: true });
        }
        fetchCondos();
      }
    } catch (err) {
      console.error('Failed to update condo:', err);
      setFormError(err.response?.data?.error || 'Failed to update condo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCondo = async (condoId, condoName) => {
    if (!window.confirm(`Are you sure you want to delete "${condoName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setSuccessMessage(null);

    try {
      const response = await axios.delete(
        `${getApiBaseUrl()}/api/admin/condos/${condoId}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      if (response.data.success) {
        navigate('/admin');
      }
    } catch (err) {
      console.error('Failed to delete condo:', err);
      alert(err.response?.data?.error || 'Failed to delete condo');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (condo) => {
    setEditingCondo(condo);
    setFormData({
      name: condo.name,
      address: condo.address,
      units: condo.units.toString(),
      features: condo.features || []
    });
    setCondoFormFeatureInput('');
    setShowAddForm(false);
    setFormError(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">Admin Portal</h1>
          <p className="text-muted-foreground mb-6">Please sign in to access the admin panel</p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Sign In
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground mt-6 block">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <div className="text-6xl mb-4">⏳</div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Account Pending Approval</h1>
          <p className="text-muted-foreground mb-6">Your account is waiting for administrator approval.</p>
          <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">You don't have admin privileges to access this page.</p>
          <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }


  const renderCondoForm = () => (
    <form onSubmit={editingCondo ? handleUpdateCondo : handleAddCondo} className="mb-6 p-4 bg-secondary rounded-lg">
      <h3 className="text-lg font-semibold mb-4 text-foreground">
        {editingCondo ? 'Edit Condo' : 'Add New Condo'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Condo Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Number of Units
          </label>
          <input
            type="number"
            name="units"
            value={formData.units}
            onChange={handleInputChange}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground"
            min="1"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">
            Address
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground"
            required
          />
        </div>

      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-foreground mb-2">Unit Features</label>
        <p className="text-xs text-muted-foreground mb-2">Define features available for units (e.g. AC, EV_CHARGER, ROOFTOP_IRRIGATION)</p>
        {formData.features.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {formData.features.map(f => (
              <span key={f} className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm text-foreground">
                {f}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, features: prev.features.filter(x => x !== f) }))}
                  className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                >×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={condoFormFeatureInput}
            onChange={(e) => setCondoFormFeatureInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const val = condoFormFeatureInput.trim().toUpperCase();
                if (val && !formData.features.includes(val)) {
                  setFormData(prev => ({ ...prev, features: [...prev.features, val] }));
                }
                setCondoFormFeatureInput('');
              }
            }}
            placeholder="e.g. AC, EV_CHARGER"
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground text-sm"
          />
          <button
            type="button"
            onClick={() => {
              const val = condoFormFeatureInput.trim().toUpperCase();
              if (val && !formData.features.includes(val)) {
                setFormData(prev => ({ ...prev, features: [...prev.features, val] }));
              }
              setCondoFormFeatureInput('');
            }}
            className="bg-secondary text-foreground px-3 py-2 rounded-lg hover:bg-secondary/80 text-sm"
          >
            Add
          </button>
        </div>
      </div>

      {formError && (
        <div className="mt-4 bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
          {formError}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Saving...' : (editingCondo ? 'Update Condo' : 'Add Condo')}
        </button>
        <button
          type="button"
          onClick={editingCondo ? () => navigate(`/admin/${editingCondo.slug}`) : resetForm}
          className="bg-muted text-muted-foreground px-6 py-2 rounded-lg hover:bg-muted/80 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  const renderSuccessMessage = () => successMessage && (
    <div role="status" className="bg-success/20 border border-success text-success px-4 py-3 rounded mb-6">
      {successMessage}
    </div>
  );

  if (!condoSlug) {
    if (!condosLoaded) {
      return (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }
    const resumeCondo = searchParams.get('resume') ? condos.find(c => c._id === lastCondoId) : null;
    const target = condos.length === 1 ? condos[0] : resumeCondo;
    const requestedTab = ADMIN_TABS.some(t => t.key === searchParams.get('tab')) ? `/${searchParams.get('tab')}` : '';
    if (target) {
      return <Navigate to={`/admin/${target.slug}${requestedTab}`} replace />;
    }

    return (
      <div className="min-h-screen bg-background">
        <div className="px-4 md:px-8 py-6 pb-20 md:pb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div>
              <h1 className="text-xl font-bold text-foreground">Admin</h1>
              <p className="text-muted-foreground mt-1">Choose a condo to manage</p>
            </div>
            {!showAddForm && (
              <button
                onClick={() => {
                  setShowAddForm(true);
                  setSuccessMessage(null);
                }}
                className="w-full sm:w-auto bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                + New Condo
              </button>
            )}
          </div>

          {renderSuccessMessage()}
          {showAddForm && renderCondoForm()}

          {condos.length === 0 ? (
            <div className="bg-card rounded-lg border border-border text-center py-8 text-muted-foreground">
              No condos yet. Add your first condo to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {condos.map((c) => (
                <Link
                  key={c._id}
                  to={`/admin/${c.slug}${requestedTab}`}
                  className="block bg-card rounded-lg border border-border p-4 hover:border-primary transition-colors"
                >
                  <h2 className="text-lg font-bold text-foreground">{c.name}</h2>
                  <p className="text-muted-foreground mt-1 text-sm">{c.address}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{c.units} units · /{c.slug}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!condo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        {condosLoaded ? (
          <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
            <h1 className="text-xl font-bold text-foreground mb-4">Condo not found</h1>
            <Link to="/admin" className="text-primary hover:underline">← All condos</Link>
          </div>
        ) : (
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        )}
      </div>
    );
  }

  // Self-management tabs (context/SelfManagement.md) appear only while the switch isn't off.
  const visibleTabs = ADMIN_TABS.filter(t => !t.selfManagement || (condo.selfManagement?.mode || 'off') !== 'off');

  if (tabParam && !['settings', 'search'].includes(tabParam) && !visibleTabs.some(t => t.key === tabParam)) {
    return <Navigate to={`/admin/${condo.slug}`} replace />;
  }


  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 md:px-8 py-6 pb-20 md:pb-8">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
          <div className="min-w-0 shrink-0">
            <p className="text-xs text-muted-foreground">Admin</p>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-foreground truncate">{condo.name}</h1>
              <CondoSwitcher condos={condos} currentSlug={condo.slug} tab={tabParam} />
            </div>
          </div>
          <div className="flex-1 flex lg:justify-end">
            <AdminSearch condo={condo} />
          </div>
          <div className="flex gap-2 shrink-0">
            <Link
              to={`/admin/${condo.slug}/settings`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs md:text-sm transition-colors ${
                tab === 'settings' ? 'border-primary text-primary' : 'border-border text-foreground hover:bg-muted'
              }`}
            >
              <Settings className="h-4 w-4" /> Condo settings
            </Link>
            <Link
              to={`/${condo.slug}`}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-foreground hover:bg-muted text-xs md:text-sm transition-colors"
            >
              <ExternalLink className="h-4 w-4" /> View as resident
            </Link>
          </div>
        </div>

        <nav aria-label="Admin sections" className="flex gap-1 overflow-x-auto border-b border-border mb-6">
          {visibleTabs.map(({ key, label, badge, quiet }) => (
            <NavLink
              key={key}
              to={key === 'dashboard' ? `/admin/${condo.slug}` : `/admin/${condo.slug}/${key}`}
              end
              className={() => `flex items-center gap-2 whitespace-nowrap px-4 py-2 border-b-2 text-sm transition-colors ${
                tab === key ? 'border-primary text-foreground font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
              {tabCounts[badge] > 0 && (
                <span className={`min-w-6 px-2 rounded-full text-xs text-center ${quiet ? 'bg-secondary text-foreground' : 'bg-notificationBadge text-notificationBadge-foreground'}`}>
                  {tabCounts[badge]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {renderSuccessMessage()}

        {tab === 'settings' && (
          <div className="bg-card rounded-lg shadow-md p-4 md:p-6 mb-6 border border-border">
            <h2 className="text-xl font-bold text-foreground mb-4">Condo settings</h2>
            {editingCondo && renderCondoForm()}
            <SelfManagementSettings
              key={condo.selfManagement?.mode || 'off'}
              condo={condo}
              notify={setSuccessMessage}
              onChanged={(updated) => setCondos(list => list.map(c => (c._id === updated._id ? { ...c, selfManagement: updated.selfManagement } : c)))}
            />
            <div className="border-t border-border pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">Deleting hides this condo and all its pages.</p>
              <button
                onClick={() => handleDeleteCondo(condo._id, condo.name)}
                className="text-destructive hover:text-destructive/80 px-3 py-2 rounded border border-destructive hover:bg-destructive/10 transition-colors text-sm"
              >
                Delete Condo
              </button>
            </div>
          </div>
        )}

        {tab === 'search' && <SearchResultsTab condo={condo} />}

        {tab === 'approvals' && (
          <ApprovalsPanel condoId={condo._id} onCountsChange={refreshCounts} />
        )}

        {visibleTabs.filter(t => t.key === tab && t.component).map(({ key, component: TabComponent }) => (
          <TabComponent key={key} condo={condo} notify={setSuccessMessage} onCountsChange={refreshCounts} />
        ))}
      </div>
    </div>
  );
}

// Keyed by condo so switching condos starts from a clean slate (no open forms or edits carried over).
function AdminRoute() {
  const { condoSlug } = useParams();
  return <AdminPage key={condoSlug || ''} />;
}

export default AdminRoute;
